import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Marker, Polyline, Popup, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { renderToStaticMarkup } from "react-dom/server";
import { toast } from "sonner";
import * as LucideIcons from "lucide-react";
import { Navigation, Locate, Loader2, Crosshair, Info, MapPin, Search, ExternalLink, Route as RouteIcon, X } from "lucide-react";
import { api } from "@/lib/api";
import { AMENITY_KINDS } from "@/lib/theme";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const OKC = { lat: 35.4676, lng: -97.5164 };
const TILE_KEY = "rb_map_custom_tiles";

const PROVIDERS = [
  { key: "internal", label: "RigBooks Internal (OpenStreetMap)" },
  { key: "google", label: "Google Maps (navigation)" },
  { key: "apple", label: "Apple Maps (navigation)" },
  { key: "custom", label: "Custom tiles (paid provider)" },
];

const PARKING_COLORS = { open: "#00E676", limited: "#F59E0B", full: "#EF4444" };

// Per-category pin colour + the lucide icon each amenity kind renders inside its map pin.
const KIND_COLOR = {
  parking: "#00E676", fuel: "#3B82F6", truck_stop: "#F59E0B", food: "#EF4444",
  shower: "#A855F7", lodging: "#22D3EE", weigh_station: "#EAB308", repair: "#F97316",
};

// Build a teardrop map pin (L.divIcon) with the category's lucide glyph inside it.
function makePinIcon(kind) {
  const meta = AMENITY_KINDS.find((a) => a.key === kind);
  const color = KIND_COLOR[kind] || "#00E676";
  const IconComp = (meta && LucideIcons[meta.icon]) || LucideIcons.MapPin;
  const glyph = renderToStaticMarkup(<IconComp color="#0B1220" size={16} strokeWidth={2.6} />);
  const html = `
    <div style="position:relative;width:32px;height:40px;">
      <div style="position:absolute;top:0;left:1px;width:30px;height:30px;border-radius:50% 50% 50% 0;transform:rotate(45deg);background:${color};border:2px solid #fff;box-shadow:0 3px 6px rgba(0,0,0,.45);"></div>
      <div style="position:absolute;top:6px;left:7px;width:18px;height:18px;display:flex;align-items:center;justify-content:center;">${glyph}</div>
    </div>`;
  return L.divIcon({ html, className: "rig-pin", iconSize: [32, 40], iconAnchor: [16, 38], popupAnchor: [0, -34] });
}

// Distinct destination pin for a user-dropped point.
const DROP_ICON = L.divIcon({
  className: "rig-pin",
  html: `
    <div style="position:relative;width:32px;height:40px;">
      <div style="position:absolute;top:0;left:1px;width:30px;height:30px;border-radius:50% 50% 50% 0;transform:rotate(45deg);background:#EF4444;border:2px solid #fff;box-shadow:0 3px 6px rgba(0,0,0,.45);"></div>
      <div style="position:absolute;top:8px;left:11px;width:10px;height:10px;border-radius:50%;background:#fff;"></div>
    </div>`,
  iconSize: [32, 40], iconAnchor: [16, 38], popupAnchor: [0, -34],
});

// Invisible layer that turns a map click into a dropped destination pin.
function MapClick({ onDrop }) {
  useMapEvents({ click(e) { onDrop({ lat: e.latlng.lat, lng: e.latlng.lng }); } });
  return null;
}

// US truckers think in miles — the backend returns km, so we convert for display
// (matches the mobile app, which also renders miles).
const KM_TO_MI = 0.621371;
const fmtMi = (km) => `${((km || 0) * KM_TO_MI).toFixed(1)} mi`;

// Reject bogus OSM coordinates (null island / out-of-range) before they reach
// the map markers or the "Navigate" deep-link.
function isValidCoord(lat, lng) {
  if (typeof lat !== "number" || typeof lng !== "number") return false;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return false;
  if (Math.abs(lat) < 0.0001 && Math.abs(lng) < 0.0001) return false;
  return true;
}

function navUrl(provider, lat, lng, name = "") {
  const q = encodeURIComponent(name);
  if (provider === "google") return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
  if (provider === "apple") return `https://maps.apple.com/?daddr=${lat},${lng}&q=${q}`;
  return `https://www.openstreetmap.org/directions?to=${lat}%2C${lng}`;
}

// Placeholder/junk OSM names permanently filtered so no "unknown" suggestions surface.
const BAD_NAMES = new Set([
  "", "unknown", "unnamed", "unknown location", "unnamed road",
  "unnamed street", "n/a", "na", "none", "null", "-", "untitled",
]);

function tagPills(tags) {
  const pills = [];
  if (tags?.hgv === "yes") pills.push(["TRUCK OK", "#00E676"]);
  if (tags?.["fuel:diesel"] === "yes" || tags?.["fuel:HGV_diesel"] === "yes") pills.push(["DIESEL", "#3B82F6"]);
  if (tags?.shower === "yes") pills.push(["SHOWER", "#A855F7"]);
  if (tags?.wifi) pills.push(["WIFI", "#F59E0B"]);
  return pills;
}

function Recenter({ center }) {
  const map = useMap();
  useEffect(() => {
    map.setView([center.lat, center.lng], map.getZoom() < 9 ? 11 : map.getZoom());
  }, [center, map]);
  return null;
}

export default function MapPage() {
  const [provider, setProvider] = useState("internal");
  const [customTiles, setCustomTiles] = useState(localStorage.getItem(TILE_KEY) || "");
  const [kind, setKind] = useState("parking");
  const [radius, setRadius] = useState(25);
  const [center, setCenter] = useState(OKC);
  const [centerLabel, setCenterLabel] = useState("");
  const [results, setResults] = useState([]);
  const [parking, setParking] = useState([]);
  const [loading, setLoading] = useState(false);
  const [locating, setLocating] = useState(false);
  const [reportStop, setReportStop] = useState(null);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [dropped, setDropped] = useState(null);
  const [route, setRoute] = useState(null);
  const [routing, setRouting] = useState(false);
  const mapRef = useRef(null);
  const autoLocated = useRef(false);
  const currentIcon = useMemo(() => makePinIcon(kind), [kind]);

  const fetchData = useCallback(async (c, k, r) => {
    setLoading(true);
    try {
      const km = Math.max(1, Math.round(r / KM_TO_MI));
      const requests = [api(`/amenities/nearby?lat=${c.lat}&lng=${c.lng}&kind=${k}&radius_km=${km}`)];
      if (k === "parking") requests.push(api(`/parking/nearby?lat=${c.lat}&lng=${c.lng}&radius_km=${km}`));
      const [amen, prk] = await Promise.all(requests);
      setResults((amen.results || []).filter((r) => isValidCoord(r.lat, r.lng) && r.name && !BAD_NAMES.has(r.name.trim().toLowerCase())));
      setParking(k === "parking" ? (prk?.stops || []).filter((s) => isValidCoord(s.lat, s.lng)) : []);
    } catch (e) {
      toast.error(e?.message || "Map service unavailable. Try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = "Commercial Map · RigBooks";
    fetchData(center, kind, radius);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center, kind]);

  // Resolve the current map center to an official place name ("City, ST").
  useEffect(() => {
    let cancelled = false;
    api(`/geo/reverse?lat=${center.lat}&lng=${center.lng}`)
      .then((g) => { if (!cancelled) setCenterLabel(g?.label || ""); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [center]);

  const locate = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported by this browser.");
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCenter({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocating(false);
      },
      () => {
        toast.error("Location denied — showing Oklahoma City (US trucking center).");
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };

  // Try to center on the driver's real location once on open; silently keep the
  // Oklahoma City default if permission is denied or unavailable.
  useEffect(() => {
    if (autoLocated.current || !navigator.geolocation) return;
    autoLocated.current = true;
    navigator.geolocation.getCurrentPosition(
      (pos) => setCenter({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {},
      { enableHighAccuracy: true, timeout: 8000 },
    );
  }, []);

  const pickPlace = (p) => {
    setCenter({ lat: p.lat, lng: p.lng });
    setCenterLabel(p.label || "");
    setSearchResults([]);
    setSearchQ(p.label || "");
  };

  const runSearch = async (e) => {
    e?.preventDefault?.();
    const q = searchQ.trim();
    if (q.length < 2) return;
    setSearching(true);
    try {
      const d = await api(`/geo/search?q=${encodeURIComponent(q)}`);
      const rs = d.results || [];
      if (rs.length === 0) toast.error("No place found for that search.");
      else if (rs.length === 1) pickPlace(rs[0]);
      else setSearchResults(rs);
    } catch {
      toast.error("Search failed. Try again.");
    } finally {
      setSearching(false);
    }
  };

  const getDirections = async (dest, label) => {
    if (!isValidCoord(dest.lat, dest.lng)) return;
    setRouting(true);
    try {
      const d = await api(`/route?from_lat=${center.lat}&from_lng=${center.lng}&to_lat=${dest.lat}&to_lng=${dest.lng}`);
      setRoute({ ...d, label: label || "Dropped pin", dest });
      const map = mapRef.current;
      if (map && d.geometry?.length) {
        map.closePopup();
        map.fitBounds(d.geometry, { padding: [50, 50] });
      }
    } catch (e) {
      toast.error(e?.message || "Could not get directions.");
    } finally {
      setRouting(false);
    }
  };

  const clearRoute = () => setRoute(null);

  const searchArea = () => {
    const map = mapRef.current;
    if (!map) return;
    const c = map.getCenter();
    setCenter({ lat: c.lat, lng: c.lng });
  };

  const applyRadius = () => fetchData(center, kind, radius);

  const submitReport = async (payload) => {
    try {
      await api("/parking/report", { method: "POST", body: payload });
      toast.success("Thanks — your parking report helps other drivers.");
      setReportStop(null);
      fetchData(center, kind, radius);
    } catch (e) {
      toast.error(e?.message || "Couldn't submit report.");
    }
  };

  const tileUrl =
    provider === "custom" && customTiles
      ? customTiles
      : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";

  return (
    <div className="space-y-5">
      {/* Current area */}
      <div
        className="flex items-center gap-2 rounded-xl border border-rig-border bg-rig-surface px-4 py-2.5"
        data-testid="map-location-label"
      >
        <MapPin className="h-4 w-4 text-rig-accent" />
        <span className="text-xs text-rig-dim">Showing near</span>
        <span className="text-sm font-semibold text-white">{centerLabel || "Locating…"}</span>
      </div>
      {/* Controls */}
      <div className="grid gap-4 rounded-2xl border border-rig-border bg-rig-surface p-4 lg:grid-cols-[1fr_auto]">
        <div className="space-y-3">
          <form onSubmit={runSearch} className="relative">
            <div className="flex gap-2">
              <Input
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                placeholder="Search a city, address or truck stop…"
                data-testid="map-search-input"
                className="h-10 border-rig-borderStrong bg-rig-bg text-white placeholder:text-rig-mute"
              />
              <Button
                type="submit"
                disabled={searching}
                data-testid="map-search-btn"
                className="h-10 shrink-0 bg-rig-accent px-4 text-rig-accentText hover:bg-rig-accentDark"
              >
                {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
            </div>
            {searchResults.length > 0 && (
              <div className="absolute z-[1000] mt-1 w-full overflow-hidden rounded-xl border border-rig-border bg-rig-surface shadow-2xl" data-testid="map-search-results">
                {searchResults.map((p, i) => (
                  <button
                    key={p.place_id ?? `sr-${i}`}
                    type="button"
                    onClick={() => pickPlace(p)}
                    data-testid={`map-search-result-${i}`}
                    className="block w-full truncate border-b border-rig-border px-3 py-2 text-left text-sm text-white last:border-b-0 hover:bg-rig-surface2"
                  >
                    {p.display_name}
                  </button>
                ))}
              </div>
            )}
          </form>
          <div className="flex flex-wrap gap-2">
            {AMENITY_KINDS.map((a) => (
              <button
                key={a.key}
                onClick={() => setKind(a.key)}
                data-testid={`amenity-${a.key}`}
                className={`rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-colors ${
                  kind === a.key
                    ? "border-rig-accent bg-rig-accent text-rig-accentText"
                    : "border-rig-border bg-rig-bg text-rig-dim hover:text-white"
                }`}
              >
                {a.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="mb-1 flex justify-between text-xs text-rig-dim">
                <span className="font-display uppercase tracking-widest">Radius</span>
                <span className="text-white">{radius} mi</span>
              </div>
              <Slider
                value={[radius]}
                min={5}
                max={50}
                step={5}
                onValueChange={(v) => setRadius(v[0])}
                onValueCommit={applyRadius}
                data-testid="radius-slider"
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2 lg:w-72">
          <div>
            <Label className="font-display text-xs uppercase tracking-widest text-rig-dim">Map Provider</Label>
            <Select value={provider} onValueChange={setProvider}>
              <SelectTrigger data-testid="map-provider" className="mt-1.5 h-10 border-rig-borderStrong bg-rig-bg text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-rig-border bg-rig-surface text-white">
                {PROVIDERS.map((p) => (
                  <SelectItem key={p.key} value={p.key} className="focus:bg-rig-surface2">{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {provider === "custom" && (
            <Input
              value={customTiles}
              onChange={(e) => {
                setCustomTiles(e.target.value);
                localStorage.setItem(TILE_KEY, e.target.value);
              }}
              placeholder="https://tiles.example.com/{z}/{x}/{y}.png"
              className="h-10 border-rig-borderStrong bg-rig-bg text-xs text-white placeholder:text-rig-mute"
            />
          )}
          <div className="flex gap-2">
            <Button
              onClick={locate}
              disabled={locating}
              data-testid="locate-btn"
              variant="outline"
              className="h-10 flex-1 border-rig-borderStrong bg-rig-bg text-white hover:bg-rig-surface2"
            >
              {locating ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Locate className="mr-1 h-4 w-4" />}
              My Location
            </Button>
            <Button
              onClick={searchArea}
              variant="outline"
              className="h-10 flex-1 border-rig-borderStrong bg-rig-bg text-white hover:bg-rig-surface2"
            >
              <Crosshair className="mr-1 h-4 w-4" /> Search Area
            </Button>
          </div>
        </div>
      </div>

      {provider !== "internal" && provider !== "custom" && (
        <div className="flex items-center gap-2 rounded-xl border border-rig-border bg-rig-surface px-4 py-2.5 text-xs text-rig-dim">
          <Info className="h-4 w-4 text-rig-blue" />
          Map tiles render with OpenStreetMap; "Navigate" opens {provider === "google" ? "Google Maps" : "Apple Maps"}.
        </div>
      )}

      {/* Map + list */}
      <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
        <div className="h-[360px] overflow-hidden rounded-2xl border border-rig-border lg:h-[560px]">
          <MapContainer
            center={[center.lat, center.lng]}
            zoom={11}
            style={{ height: "100%", width: "100%" }}
            ref={mapRef}
          >
            <TileLayer url={tileUrl} attribution="&copy; OpenStreetMap contributors" />
            <Recenter center={center} />
            <MapClick onDrop={setDropped} />
            <CircleMarker
              center={[center.lat, center.lng]}
              radius={8}
              pathOptions={{ color: "#3B82F6", fillColor: "#3B82F6", fillOpacity: 0.9 }}
            >
              <Popup>You are here</Popup>
            </CircleMarker>
            {route?.geometry?.length > 0 && (
              <Polyline positions={route.geometry} pathOptions={{ color: "#3B82F6", weight: 5, opacity: 0.85 }} />
            )}
            {dropped && (
              <Marker position={[dropped.lat, dropped.lng]} icon={DROP_ICON}>
                <Popup>
                  <div style={{ minWidth: 170 }}>
                    <div style={{ fontWeight: 700, marginBottom: 6 }}>Dropped pin</div>
                    <button
                      onClick={() => getDirections(dropped, "Dropped pin")}
                      style={{ display: "block", width: "100%", marginBottom: 6, padding: "6px 8px", background: "#00E676", color: "#0B1220", border: "none", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer" }}
                    >
                      Directions here →
                    </button>
                    <button
                      onClick={() => setDropped(null)}
                      style={{ display: "block", width: "100%", padding: "6px 8px", background: "transparent", color: "#94A3B8", border: "1px solid #334155", borderRadius: 8, fontSize: 12, cursor: "pointer" }}
                    >
                      Clear pin
                    </button>
                  </div>
                </Popup>
              </Marker>
            )}
            {results.map((r) => (
              <Marker key={r.osm_id} position={[r.lat, r.lng]} icon={currentIcon}>
                <Popup>
                  <div style={{ minWidth: 190 }}>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>{r.name}</div>
                    <div style={{ fontSize: 12, color: "#94A3B8", marginBottom: 8 }}>
                      {fmtMi(r.distance_km)} away
                    </div>
                    <button
                      onClick={() => getDirections({ lat: r.lat, lng: r.lng }, r.name)}
                      style={{ display: "block", width: "100%", marginBottom: 6, padding: "6px 8px", background: "#00E676", color: "#0B1220", border: "none", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer" }}
                    >
                      Directions →
                    </button>
                    <a
                      href={navUrl(provider, r.lat, r.lng, r.name)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#94A3B8", fontSize: 12 }}
                    >
                      Open in external maps ↗
                    </a>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        <div className="max-h-[560px] space-y-4 overflow-y-auto pr-1">
          {route && (
            <div className="rounded-2xl border border-rig-blue/50 bg-rig-surface p-4" data-testid="directions-panel">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 text-rig-blue">
                    <RouteIcon className="h-4 w-4" />
                    <span className="font-display text-lg font-bold text-white">{route.distance_mi} mi · {route.duration_min} min</span>
                  </div>
                  <div className="mt-0.5 truncate text-xs text-rig-dim">to {route.label}</div>
                </div>
                <Button onClick={clearRoute} variant="outline" size="sm" data-testid="clear-route-btn" className="h-8 shrink-0 border-rig-borderStrong bg-transparent text-xs text-white hover:bg-rig-surface2">
                  <X className="mr-1 h-3.5 w-3.5" /> Clear
                </Button>
              </div>
              <div className="mt-3 max-h-56 space-y-0.5 overflow-y-auto" data-testid="directions-steps">
                {route.steps.map((s, i) => (
                  <div key={`step-${i}-${(s.instruction || "").slice(0, 24)}`} className="flex items-center justify-between gap-3 border-b border-rig-border/40 py-1.5 text-sm last:border-b-0">
                    <span className="text-white">{s.instruction}</span>
                    <span className="shrink-0 text-xs text-rig-dim">{fmtMi(s.distance_m / 1000)}</span>
                  </div>
                ))}
              </div>
              <p className="mt-2 text-[11px] text-rig-mute">Driving route (car profile) — always verify truck height / weight / restrictions.</p>
            </div>
          )}

          <p className="flex items-center gap-1.5 text-xs text-rig-mute" data-testid="drop-pin-hint">
            <MapPin className="h-3.5 w-3.5" /> Tap anywhere on the map to drop a pin and get directions.
          </p>

          {kind === "parking" && (
            <div>
              <h3 className="mb-2 font-display text-xs font-bold uppercase tracking-[0.18em] text-rig-accent">
                Live Parking Reports · Last 12h
              </h3>
              {parking.length === 0 ? (
                <p className="rounded-xl border border-rig-border bg-rig-surface px-4 py-3 text-sm text-rig-dim">
                  No community parking reports nearby yet. Be the first to report.
                </p>
              ) : (
                <div className="space-y-2">
                  {parking.map((s) => (
                    <div key={`${s.stop_name}-${s.lat}`} className="rounded-xl border border-rig-border bg-rig-surface p-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="truncate font-semibold text-white">{s.stop_name}</div>
                          <div className="text-xs text-rig-dim">
                            {fmtMi(s.distance_km)} · {s.report_count} report{s.report_count === 1 ? "" : "s"}
                          </div>
                        </div>
                        <span
                          className="shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase"
                          style={{ background: `${PARKING_COLORS[s.consensus]}22`, color: PARKING_COLORS[s.consensus] }}
                        >
                          {s.consensus}
                        </span>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setReportStop({ stop_name: s.stop_name, lat: s.lat, lng: s.lng, osm_id: s.osm_id })}
                        className="mt-2 h-8 border-rig-borderStrong bg-transparent text-xs text-white hover:bg-rig-surface2"
                      >
                        Report
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div>
            <h3 className="mb-2 flex items-center justify-between font-display text-xs font-bold uppercase tracking-[0.18em] text-rig-dim">
              <span>{loading ? "Searching…" : `${results.length} nearby`}</span>
              {loading && <Loader2 className="h-4 w-4 animate-spin text-rig-accent" />}
            </h3>
            <div className="space-y-2">
              {results.map((r) => {
                const pills = tagPills(r.tags);
                return (
                  <div key={r.osm_id} data-testid={`amenity-result-${r.osm_id}`} className="rounded-xl border border-rig-border bg-rig-surface p-3.5">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="truncate font-semibold text-white">{r.name}</div>
                        <div className="text-xs text-rig-dim">{fmtMi(r.distance_km)} away{r.tags?.addr ? ` · ${r.tags.addr}` : ""}</div>
                      </div>
                      <div className="flex shrink-0 items-center gap-1.5">
                        <Button
                          size="sm"
                          onClick={() => getDirections({ lat: r.lat, lng: r.lng }, r.name)}
                          disabled={routing}
                          data-testid={`directions-${r.osm_id}`}
                          className="h-8 bg-rig-accent text-xs font-bold text-rig-accentText hover:bg-rig-accentDark"
                        >
                          <Navigation className="mr-1 h-3.5 w-3.5" /> Directions
                        </Button>
                        <a href={navUrl(provider, r.lat, r.lng, r.name)} target="_blank" rel="noopener noreferrer" title="Open in external maps">
                          <Button size="sm" variant="outline" className="h-8 border-rig-borderStrong bg-transparent px-2 text-white hover:bg-rig-surface2">
                            <ExternalLink className="h-3.5 w-3.5" />
                          </Button>
                        </a>
                      </div>
                    </div>
                    {(pills.length > 0 || kind === "parking") && (
                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        {pills.map(([label, color]) => (
                          <span key={label} className="rounded px-2 py-0.5 text-[10px] font-bold" style={{ background: `${color}22`, color }}>
                            {label}
                          </span>
                        ))}
                        {kind === "parking" && (
                          <button
                            onClick={() => setReportStop({ stop_name: r.name, lat: r.lat, lng: r.lng, osm_id: r.osm_id })}
                            className="text-[11px] font-semibold text-rig-blue hover:underline"
                          >
                            Report availability
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {!loading && results.length === 0 && (
                <p className="rounded-xl border border-dashed border-rig-borderStrong bg-rig-surface px-4 py-6 text-center text-sm text-rig-dim">
                  Nothing found in this radius. Widen the radius or move the map, then "Search Area".
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <ParkingReportDialog stop={reportStop} onClose={() => setReportStop(null)} onSubmit={submitReport} />
    </div>
  );
}

function ParkingReportDialog({ stop, onClose, onSubmit }) {
  const [status, setStatus] = useState("open");
  const [spots, setSpots] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (stop) {
      setStatus("open");
      setSpots("");
      setNotes("");
    }
  }, [stop]);

  if (!stop) return null;

  const submit = () => {
    onSubmit({
      osm_id: stop.osm_id || null,
      stop_name: stop.stop_name,
      lat: stop.lat,
      lng: stop.lng,
      status,
      spots_open: spots ? parseInt(spots, 10) : null,
      notes,
    });
  };

  return (
    <Dialog open={!!stop} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-rig-border bg-rig-surface">
        <DialogHeader>
          <DialogTitle className="font-display tracking-wide text-white">Report parking · {stop.stop_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-2">
            {["open", "limited", "full"].map((s) => (
              <button
                key={s}
                onClick={() => setStatus(s)}
                data-testid={`parking-status-${s}`}
                className={`flex-1 rounded-xl border px-3 py-2.5 text-sm font-bold uppercase tracking-wide transition-colors ${
                  status === s
                    ? "border-transparent text-rig-bg"
                    : "border-rig-borderStrong bg-rig-bg text-rig-dim"
                }`}
                style={status === s ? { background: PARKING_COLORS[s] } : {}}
              >
                {s}
              </button>
            ))}
          </div>
          <div>
            <Label className="font-display text-xs uppercase tracking-widest text-rig-dim">Open spots (optional)</Label>
            <Input
              type="number"
              value={spots}
              onChange={(e) => setSpots(e.target.value)}
              placeholder="e.g. 8"
              className="mt-1.5 h-11 border-rig-borderStrong bg-rig-bg text-white placeholder:text-rig-mute"
            />
          </div>
          <div>
            <Label className="font-display text-xs uppercase tracking-widest text-rig-dim">Note (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Back lot full, fuel island side open…"
              className="mt-1.5 border-rig-borderStrong bg-rig-bg text-white placeholder:text-rig-mute"
            />
          </div>
          <Button
            onClick={submit}
            data-testid="submit-parking-report"
            className="h-12 w-full bg-rig-accent font-display text-sm font-bold uppercase tracking-widest text-rig-accentText hover:bg-rig-accentDark"
          >
            Submit Report
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
